/**
 * The proof panels on the front door — drawn stand-ins for the sample answers
 * in front-door-vision.md § "Proof examples" (E1–E4).
 *
 * ⚠ These are illustrations, not the app's output. Each is replaced by a
 * screenshot (or, for E1, a short video) of the real app marking our team's
 * handwritten sample answers — and only then does its caption gain "Marking by
 * BuzzBrain, unedited." Until then the caption says no more than is true.
 */

import type { ReactNode } from 'react';
import './front-door.css';

const SAMPLE_CAPTION = 'Sample answer written by our team.';

export function CoverageChips({ label }: { label?: string }) {
  return (
    <ul className="fd-chips" aria-label={label ?? 'What BuzzBrain covers'}>
      <li className="fd-chip fd-chip--strong">Science</li>
      <li className="fd-chip fd-chip--strong">Maths</li>
      <li className="fd-chip fd-chip--strong">Classes 8–12</li>
      <li className="fd-chip">CBSE</li>
      <li className="fd-chip">ICSE / ISC</li>
      <li className="fd-chip">State boards</li>
    </ul>
  );
}

function Flag({ children }: { children: ReactNode }) {
  return <span className="fd-flag">{children}</span>;
}

function Slip({ children }: { children: ReactNode }) {
  return <span className="fd-slip">{children}</span>;
}

/** E2 — two mistakes that cancel, so the final answer matches the key. */
export function WorkingSample() {
  return (
    <figure className="fd-figure">
      <div className="fd-sheet">
        <p className="fd-sheet-q">
          Class 12 Maths · y = 5x − 2x³, and x increases at 2 units/s. How fast is the slope changing at x = 2?
        </p>
        <div className="fd-hand">
          <div className="fd-line">
            <span>dy/dx = 5 <Slip>+ 6x²</Slip></span>
            <Flag>First issue here · the derivative of −2x³ is −6x²</Flag>
          </div>
          <div className="fd-line">d/dt (dy/dx) = 12x · dx/dt</div>
          <div className="fd-line">= 12 × 2 × 2 = 48</div>
          <div className="fd-line">
            <Slip>slope is decreasing, so −48</Slip>
            <Flag>Sign changed with no reason</Flag>
          </div>
        </div>
        <div className="fd-verdicts">
          <span className="fd-verdict">Answer key: −48 · matches</span>
          <span className="fd-verdict fd-verdict--bb">BuzzBrain: part marks · 2 mistakes found</span>
        </div>
      </div>
      <figcaption className="fd-caption">{SAMPLE_CAPTION}</figcaption>
    </figure>
  );
}

/** E3 — one student's feedback, beside the line it came from. */
export function FeedbackSample() {
  return (
    <figure className="fd-figure">
      <div className="fd-pair">
        <div className="fd-sheet">
          <p className="fd-sheet-q">
            Class 10 Chemistry · (i) Volume of 40 g of methane at STP (V.D. = 8). (ii) Moles in 160 g of NaOH.
          </p>
          <div className="fd-hand">
            <div className="fd-line">(i) n = 40 / <Slip>8</Slip> = 5 mol</div>
            <div className="fd-line">V = 5 × 22.4 = 112 L</div>
            <div className="fd-line">(ii) n = 160 / 40 = 4 mol <span className="fd-tick" aria-label="correct">✓</span></div>
          </div>
        </div>
        <div className="fd-card fd-feedback">
          <div className="fd-feedback-head"><strong>Feedback for Diya</strong><span>Q4 (i)</span></div>
          <div>
            <div className="fd-kicker fd-kicker--slip">What went wrong</div>
            <p>You used the vapour density (8) as the molar mass. Molar mass = 2 × V.D. = 16 g/mol, so 40 g is 2.5 mol, and 2.5 × 22.4 = 56 L.</p>
          </div>
          <div>
            <div className="fd-kicker">Revise first</div>
            <p>Mole concept — vapour density and molar mass.</p>
          </div>
          <div>
            <div className="fd-kicker fd-kicker--ok">Done well</div>
            <p>Part (ii) is correct: 160 g of NaOH is 4 mol.</p>
          </div>
        </div>
      </div>
      <figcaption className="fd-caption">{SAMPLE_CAPTION}</figcaption>
    </figure>
  );
}

type Cell = 'ok' | { tag: string; strong?: boolean };
const CLASS_ROWS: { name: string; cells: [Cell, Cell, Cell] }[] = [
  { name: 'Aarav', cells: ['ok', 'ok', 'ok'] },
  { name: 'Diya', cells: ['ok', { tag: 'mg', strong: true }, { tag: 'mg', strong: true }] },
  { name: 'Kabir', cells: [{ tag: 'units' }, { tag: 'mg', strong: true }, { tag: 'mg', strong: true }] },
  { name: 'Meera', cells: ['ok', { tag: 'mg', strong: true }, { tag: 'mg', strong: true }] },
  { name: 'Rohan', cells: [{ tag: 'mass' }, 'ok', 'ok'] },
];

/** E4 — the class view: one mistake shared by three students is one topic to reteach. */
export function ClassViewSample() {
  return (
    <figure className="fd-figure">
      <div className="fd-pair">
        <div className="fd-card fd-table-box">
          <table className="fd-table">
            <caption>Laws of Motion — Test 1</caption>
            <thead>
              <tr><th scope="col">Student</th><th scope="col">Q1 braking</th><th scope="col">Q2 lift</th><th scope="col">Q3 rocket</th></tr>
            </thead>
            <tbody>
              {CLASS_ROWS.map((row) => (
                <tr key={row.name}>
                  <th scope="row">{row.name}</th>
                  {row.cells.map((c, i) => (
                    <td key={i}>
                      {c === 'ok'
                        ? <span className="fd-tick" aria-label="correct">✓</span>
                        : <span className={c.strong ? 'fd-tag fd-tag--strong' : 'fd-tag'}>{c.tag}</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="fd-stack">
          <div className="fd-reteach">
            <div className="fd-reteach-kicker">Reteach this · 3 of 5 students</div>
            <p className="fd-reteach-title">Leaving out the weight (mg) when a body accelerates up or down</p>
            <p className="fd-reteach-who">Diya, Kabir, Meera · Q2 lift, Q3 rocket</p>
          </div>
          <div className="fd-card fd-mini"><strong>1 student</strong> · km/h not changed to m/s (Q1)</div>
          <div className="fd-card fd-mini"><strong>1 student</strong> · driver’s mass left out (Q1)</div>
        </div>
      </div>
      <figcaption className="fd-caption">Sample answers written by our team.</figcaption>
    </figure>
  );
}

/** E1 — still frame for the hero until the 15-second upload-to-marked video exists. */
export function MarkedListSample() {
  const rows: [string, string, boolean][] = [
    ['Aarav', 'All correct', true],
    ['Diya', 'Q2, Q3 · weight left out', false],
    ['Kabir', 'Q1 units · Q2, Q3 weight', false],
    ['Meera', 'Q2, Q3 · weight left out', false],
    ['Rohan', 'Q1 · driver’s mass left out', false],
  ];
  return (
    <figure className="fd-figure">
      <div className="fd-card fd-marked">
        <div className="fd-marked-head">
          <strong>Laws of Motion — Test 1</strong>
          <span className="fd-pill-ok">5 of 5 marked</span>
        </div>
        <ul className="fd-marked-list">
          {rows.map(([name, note, ok]) => (
            <li key={name}>
              <span className="fd-marked-name">{name}</span>
              <span className={ok ? 'fd-marked-ok' : 'fd-marked-slip'}>{note}</span>
            </li>
          ))}
        </ul>
      </div>
      <figcaption className="fd-caption">Sample answers written by our team.</figcaption>
    </figure>
  );
}
